import { intl } from "dmsbasereact";
import React, { useRef, useState, useCallback } from "react";
import {
    Chip,
    Typography,
    Box,
    Grid,
    Tooltip,
    Divider,
    Paper
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";

import {
    DmsPage,
    DmsTable,
    DmsAltBox,
    DmsModal,
    DmsButton,
    DmsTextField,
    fetchData,
    HTTP_STATUS,
    Notifications,
    DmsPermissions,
    getToken
} from "dmsbasereact";
import Swal from "sweetalert2";

import { Permissions } from "../../constants/Permissions";
import { DocumentUploadSlot } from "../../components/DocumentUploadSlot/DocumentUploadSlot";
import {
    CancellationRequestStatus,
    CancellationRequestStatusEnum,
    getCancellationRequestStatusOptions
} from "../../constants/CancellationRequestStatus";

const getCancellationRequestStatusChip = (status) => {
    return (
        <Chip
            label={CancellationRequestStatus.getText(status)}
            color={CancellationRequestStatus.getColor(status)}
            size="small"
        />
    );
};

export const DispensationCancellationRequestsPage = () => {
    const table = useRef(null);

    // Modal states
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);

    // Detail batches state (multi-batch from DispensingBatch junction)
    const [detailBatches, setDetailBatches] = useState([]);

    // Form states
    const [approvalJustification, setApprovalJustification] = useState("");
    const [rejectionJustification, setRejectionJustification] = useState("");
    const [approvalFiles, setApprovalFiles] = useState([]);
    const [rejectionFiles, setRejectionFiles] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [uploading, setUploading] = useState(false);

    // File download handler
    const handleDownload = useCallback(async (file_pk, filename) => {
        const downloadUrl = `/file/download?pk=${file_pk}`;
        const token = getToken();
        try {
            const response = await fetch(downloadUrl, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token.access_token}` }
            });
            if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename || 'download';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            Swal.fire(
                intl.get("DownloadError").d("Download Error"),
                err.message,
                "error"
            );
        }
    }, []);

    // View details
    const handleViewDetails = useCallback(async (request) => {
        try {
            const response = await fetchData(
                `/dispensation-cancellation-requests/${request.pk}`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                const requestData = response.data?.data;
                setSelectedRequest(requestData);
                setDetailModalOpen(true);

                // Fetch multi-batch data for the dispensation
                if (requestData?.dispensing?.pk) {
                    try {
                        const batchesResponse = await fetchData(
                            `/dispensing/${requestData.dispensing.pk}/dispensing-batches`,
                            "GET",
                            true
                        );
                        if (batchesResponse.status === HTTP_STATUS.OK_200) {
                            setDetailBatches(batchesResponse.data?.data || []);
                        } else {
                            setDetailBatches([]);
                        }
                    } catch {
                        setDetailBatches([]);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching request details:", error);
            Notifications.toast(
                intl.get("ErrorFetchingDetails").d("Failed to load request details"),
                Notifications.TYPE_ERROR
            );
        }
    }, []);

    const handleCloseDetailModal = useCallback(() => {
        setDetailModalOpen(false);
        setSelectedRequest(null);
        setDetailBatches([]);
    }, []);

    // Approve flow
    const handleOpenApproveModal = useCallback((request) => {
        setSelectedRequest(request);
        setApprovalJustification("");
        setApproveModalOpen(true);
    }, []);

    const handleCloseApproveModal = useCallback(() => {
        setApproveModalOpen(false);
        setSelectedRequest(null);
        setApprovalJustification("");
        setApprovalFiles([]);
    }, []);

    const handleApprove = useCallback(async () => {
        if (!selectedRequest || !approvalJustification.trim()) {
            Notifications.toast(
                intl.get("JustificationRequired").d("Justification is required"),
                Notifications.TYPE_ERROR
            );
            return;
        }

        if (approvalJustification.trim().length < 10) {
            Notifications.toast(
                intl.get("JustificationTooShort").d("Justification must be at least 10 characters"),
                Notifications.TYPE_ERROR
            );
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                justification: approvalJustification.trim(),
                file_pks: approvalFiles.map(f => f.pk)
            };

            const response = await fetchData(
                `/dispensation-cancellation-requests/${selectedRequest.pk}/approve`,
                "POST",
                true,
                payload
            );

            if (response.status === HTTP_STATUS.OK_200) {
                Notifications.toast(
                    intl.get("RequestApproved").d("Cancellation request approved successfully"),
                    Notifications.TYPE_SUCCESS
                );
                handleCloseApproveModal();
                table.current?.updateTable();
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("ErrorApprovingRequest").d("Failed to approve request"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (error) {
            console.error("Error approving request:", error);
            Notifications.toast(
                intl.get("ErrorApprovingRequest").d("Failed to approve request"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setProcessing(false);
        }
    }, [selectedRequest, approvalJustification, approvalFiles, handleCloseApproveModal]);

    // File upload handler for approval
    const handleApprovalFileSelect = useCallback(async (docName, selectedFile) => {
        if (!selectedFile) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const response = await fetchData(
                '/dispensation-cancellation-requests/upload-attachment',
                'POST',
                true,
                formData,
                false,
                null,
                null,
                null,
                'multipart/form-data',
                false
            );

            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                const uploadedFile = response.data?.data;
                if (uploadedFile) {
                    setApprovalFiles(prev => [...prev, uploadedFile]);
                    Notifications.toast(
                        intl.get("FileUploaded").d("File uploaded successfully"),
                        Notifications.TYPE_SUCCESS
                    );
                }
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("ErrorUploadingFile").d("Failed to upload file"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            Notifications.toast(
                intl.get("ErrorUploadingFile").d("Failed to upload file"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setUploading(false);
        }
    }, []);

    const handleRemoveApprovalFile = useCallback((filePk) => {
        setApprovalFiles(prev => prev.filter(f => f.pk !== filePk));
    }, []);

    // Reject flow
    const handleOpenRejectModal = useCallback((request) => {
        setSelectedRequest(request);
        setRejectionJustification("");
        setRejectModalOpen(true);
    }, []);

    const handleCloseRejectModal = useCallback(() => {
        setRejectModalOpen(false);
        setSelectedRequest(null);
        setRejectionJustification("");
        setRejectionFiles([]);
    }, []);

    const handleReject = useCallback(async () => {
        if (!selectedRequest || !rejectionJustification.trim()) {
            Notifications.toast(
                intl.get("JustificationRequired").d("Justification is required"),
                Notifications.TYPE_ERROR
            );
            return;
        }

        if (rejectionJustification.trim().length < 10) {
            Notifications.toast(
                intl.get("JustificationTooShort").d("Justification must be at least 10 characters"),
                Notifications.TYPE_ERROR
            );
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                justification: rejectionJustification.trim(),
                file_pks: rejectionFiles.map(f => f.pk)
            };

            const response = await fetchData(
                `/dispensation-cancellation-requests/${selectedRequest.pk}/reject`,
                "POST",
                true,
                payload
            );

            if (response.status === HTTP_STATUS.OK_200) {
                Notifications.toast(
                    intl.get("RequestRejected").d("Cancellation request rejected"),
                    Notifications.TYPE_SUCCESS
                );
                handleCloseRejectModal();
                table.current?.updateTable();
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("ErrorRejectingRequest").d("Failed to reject request"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (error) {
            console.error("Error rejecting request:", error);
            Notifications.toast(
                intl.get("ErrorRejectingRequest").d("Failed to reject request"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setProcessing(false);
        }
    }, [selectedRequest, rejectionJustification, rejectionFiles, handleCloseRejectModal]);

    // File upload handler for rejection
    const handleRejectionFileSelect = useCallback(async (docName, selectedFile) => {
        if (!selectedFile) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const response = await fetchData(
                '/dispensation-cancellation-requests/upload-attachment',
                'POST',
                true,
                formData,
                false,
                null,
                null,
                null,
                'multipart/form-data',
                false
            );

            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                const uploadedFile = response.data?.data;
                if (uploadedFile) {
                    setRejectionFiles(prev => [...prev, uploadedFile]);
                    Notifications.toast(
                        intl.get("FileUploaded").d("File uploaded successfully"),
                        Notifications.TYPE_SUCCESS
                    );
                }
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("ErrorUploadingFile").d("Failed to upload file"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            Notifications.toast(
                intl.get("ErrorUploadingFile").d("Failed to upload file"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setUploading(false);
        }
    }, []);

    const handleRemoveRejectionFile = useCallback((filePk) => {
        setRejectionFiles(prev => prev.filter(f => f.pk !== filePk));
    }, []);

    const header = [
        {
            label: intl.get("CancellationRequests.RequestDate").d("Request Date"),
            column_name: "requested_on",
            filter: true,
            control: "date",
            element: (row) => row.requested_on ? dayjs(row.requested_on).format("DD/MM/YYYY HH:mm") : "-"
        },
        {
            label: intl.get("PrescriptionNumber").d("Prescription Number"),
            column_name: "prescription_identifier",
            filter: true,
            element: (row) => row.dispensing?.prescription?.prescription_identifier || "-"
        },
        {
            label: intl.get("Patient").d("Patient"),
            column_name: "patient_name",
            filter: true,
            element: (row) => row.dispensing?.prescription?.patient_name || "-"
        },
        {
            label: intl.get("Pharmacist").d("Pharmacist"),
            column_name: "pharmacist_crf",
            filter: true,
            element: (row) => {
                const pharmacist = row.dispensing?.pharmacist;
                if (!pharmacist) return "-";
                return pharmacist.crf ? `${pharmacist.crf} (${pharmacist.crf_state})` : "-";
            }
        },
        {
            label: intl.get("Reason").d("Reason"),
            column_name: "request_justification",
            filter: false,
            sortable: false,
            element: (row) => {
                const justification = row.request_justification || "";
                const truncated = justification.length > 50
                    ? justification.substring(0, 50) + "..."
                    : justification;
                return (
                    <Tooltip title={justification}>
                        <span>{truncated}</span>
                    </Tooltip>
                );
            }
        },
        {
            label: intl.get("Documents").d("Documents"),
            column_name: "files",
            filter: false,
            sortable: false,
            element: (row) => {
                if (!row.files || row.files.length === 0) {
                    return "-";
                }

                return (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {row.files.map((fileAttachment, index) => {
                            const file = fileAttachment.file;
                            if (!file) return null;

                            return (
                                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                        {file.original_filename}
                                    </Typography>
                                    <Tooltip title={intl.get("Download").d("Download")}>
                                        <span>
                                            <DmsButton
                                                icon={<DownloadIcon fontSize="small" />}
                                                onClick={() => handleDownload(file.pk, file.original_filename)}
                                                variant="secondary"
                                                size="small"
                                            />
                                        </span>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Box>
                );
            }
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status",
            filter: true,
            control: "select",
            options: getCancellationRequestStatusOptions(),
            element: (row) => getCancellationRequestStatusChip(row.status)
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            sortable: false,
            element: (row) => (
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title={intl.get("ViewDetails").d("View Details")}>
                        <span>
                            <DmsButton
                                icon={<VisibilityIcon />}
                                onClick={() => handleViewDetails(row)}
                                variant="secondary"
                                size="small"
                            />
                        </span>
                    </Tooltip>
                    {row.status === CancellationRequestStatusEnum.PENDING && (
                        <>
                            <Tooltip title={intl.get("Approve").d("Approve")}>
                                <span>
                                    <DmsButton
                                        icon={<CheckCircleIcon />}
                                        onClick={() => handleOpenApproveModal(row)}
                                        variant="success"
                                        size="small"
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip title={intl.get("Reject").d("Reject")}>
                                <span>
                                    <DmsButton
                                        icon={<CancelIcon />}
                                        onClick={() => handleOpenRejectModal(row)}
                                        variant="danger"
                                        size="small"
                                    />
                                </span>
                            </Tooltip>
                        </>
                    )}
                </Box>
            )
        }
    ];

    return (
        <DmsPermissions permissions={[Permissions.DISPENSATION_CANCELLATION_APPROVE]}>
            <DmsPage name={intl.get("DispensationCancellationRequests").d("Dispensation Cancellation Requests")}>
                <DmsAltBox.Card>
                    <DmsAltBox.Header>
                        <Typography variant="h6">
                            {intl.get("CancellationRequestsList").d("Cancellation Requests")}
                        </Typography>
                    </DmsAltBox.Header>
                    <DmsAltBox.Body>
                        <DmsTable
                            header={header}
                            managed={true}
                            needs_authentication={true}
                            export_table={true}
                            ref={table}
                            url="/dispensation-cancellation-requests"
                        />
                    </DmsAltBox.Body>
                </DmsAltBox.Card>

                {/* Detail Modal */}
                <DmsModal
                    isVisible={detailModalOpen}
                    onClose={handleCloseDetailModal}
                    maxWidth="md"
                >
                    <DmsModal.Header
                        title={intl.get("RequestDetails").d("Request Details")}
                        onClose={handleCloseDetailModal}
                    />
                    <DmsModal.Body>
                        {selectedRequest && (
                            <Box sx={{ p: 2 }}>
                                {/* Request Info */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                        <Typography variant="h6">
                                            {intl.get("Request").d("Request")} #{selectedRequest.pk}
                                        </Typography>
                                        {getCancellationRequestStatusChip(selectedRequest.status)}
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                {intl.get("RequestedOn").d("Requested On")}
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedRequest.requested_on ? dayjs(selectedRequest.requested_on).format("DD/MM/YYYY HH:mm") : "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                {intl.get("RequestedBy").d("Requested By")}
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedRequest.requested_by?.user_name || "-"}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Cancellation Reason */}
                                <Box sx={{ mb: 3, p: 2, bgcolor: "warning.50", borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1, fontWeight: 600 }}>
                                        {intl.get("CancellationReason").d("Cancellation Reason")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.request_justification || "-"}
                                    </Typography>
                                </Box>

                                {/* Prescription Info */}
                                <Box sx={{ mb: 2, p: 2, bgcolor: "primary.50", borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                        {intl.get("Prescription").d("Prescription")}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">
                                                {intl.get("Number").d("Number")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {selectedRequest.dispensing?.prescription?.prescription_identifier || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">
                                                {intl.get("Patient").d("Patient")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body2">
                                                {selectedRequest.dispensing?.prescription?.patient_name || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">
                                                {intl.get("PatientCode").d("Patient Code")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body2">
                                                {selectedRequest.dispensing?.prescription?.patient_code || "-"}
                                            </Typography>
                                        </Grid>
                                        {selectedRequest.dispensing?.prescription?.dosage_description && (
                                            <>
                                                <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {intl.get("Posology").d("Posology")}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2">
                                                        {selectedRequest.dispensing.prescription.dosage_description}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Box>

                                {/* Pharmacist Info */}
                                <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                        {intl.get("Pharmacist").d("Pharmacist")}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                            <Typography variant="body2">
                                                {selectedRequest.dispensing?.pharmacist?.crf
                                                    ? `CRF: ${selectedRequest.dispensing.pharmacist.crf} (${selectedRequest.dispensing.pharmacist.crf_state})`
                                                    : "-"}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Dispensation Info */}
                                <Box sx={{ mb: 2, p: 2, bgcolor: "success.50", borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="success.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                                        {intl.get("DispensationDetails").d("Dispensation Details")}
                                    </Typography>
                                    <Grid container spacing={1} sx={{ mb: 1.5 }}>
                                        <Grid item xs={4}>
                                            <Typography variant="caption" color="text.secondary">
                                                {intl.get("DispensedOn").d("Dispensed On")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body2">
                                                {selectedRequest.dispensing?.created_on
                                                    ? dayjs(selectedRequest.dispensing.created_on).format("DD/MM/YYYY HH:mm")
                                                    : "-"}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    {/* Batches */}
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        {intl.get("Batches").d("Batches")}
                                    </Typography>
                                    {detailBatches.length > 0 ? detailBatches.map((batch, index) => (
                                        <Paper key={index} sx={{ p: 1.5, mb: 1, border: '1px solid #e0e0e0' }} elevation={0}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary">{intl.get("Number").d("Number")}</Typography>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {batch.batch_number || '-'}
                                                    </Typography>
                                                </Grid>
                                                {batch.strength && (
                                                    <>
                                                        <Grid item xs={4}>
                                                            <Typography variant="caption" color="text.secondary">{intl.get("DoseStrength").d("Dose Strength")}</Typography>
                                                        </Grid>
                                                        <Grid item xs={8}>
                                                            <Typography variant="body2">{batch.strength}</Typography>
                                                        </Grid>
                                                    </>
                                                )}
                                                <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary">{intl.get("box_count").d("Box Count")}</Typography>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2">{batch.box_count || 0}</Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary">{intl.get("capsule_count").d("Capsule Count")}</Typography>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2">{batch.capsule_count || 0}</Typography>
                                                </Grid>
                                                {batch.batch_expiration && (
                                                    <>
                                                        <Grid item xs={4}>
                                                            <Typography variant="caption" color="text.secondary">{intl.get("Expiration").d("Expiration")}</Typography>
                                                        </Grid>
                                                        <Grid item xs={8}>
                                                            <Typography variant="body2">
                                                                {dayjs(batch.batch_expiration).format('DD/MM/YYYY')}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}
                                            </Grid>
                                        </Paper>
                                    )) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                </Box>

                                {/* Review Info (if reviewed) */}
                                {(selectedRequest.status === CancellationRequestStatusEnum.APPROVED ||
                                  selectedRequest.status === CancellationRequestStatusEnum.REJECTED) && (
                                    <Box sx={{ mb: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                                        <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1.5, fontWeight: 600 }}>
                                            {intl.get("ReviewDetails").d("Review Details")}
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {intl.get("Result").d("Result")}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                {getCancellationRequestStatusChip(selectedRequest.status)}
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {intl.get("ReviewedOn").d("Reviewed On")}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography variant="body2">
                                                    {selectedRequest.reviewed_on
                                                        ? dayjs(selectedRequest.reviewed_on).format("DD/MM/YYYY HH:mm")
                                                        : "-"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {intl.get("ReviewedBy").d("Reviewed By")}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography variant="body2">
                                                    {selectedRequest.reviewed_by?.user_name || "-"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {intl.get("Justification").d("Justification")}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography variant="body2">
                                                    {selectedRequest.review_justification || "-"}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DmsModal.Body>
                </DmsModal>

                {/* Approve Modal */}
                <DmsModal
                    isVisible={approveModalOpen}
                    onClose={handleCloseApproveModal}
                    maxWidth="sm"
                >
                    <DmsModal.Header
                        title={intl.get("ApproveRequest").d("Approve Cancellation Request")}
                        onClose={handleCloseApproveModal}
                    />
                    <DmsModal.Body>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                {intl.get("ApproveRequestConfirmation").d(
                                    "Are you sure you want to approve this cancellation request? The dispensation will be canceled and stock will be restored."
                                )}
                            </Typography>
                            <DmsTextField
                                label={intl.get("ApprovalJustification").d("Approval Justification")}
                                value={approvalJustification}
                                onChange={(e) => setApprovalJustification(e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                                required
                                helperText={intl.get("MinimumCharacters").d("Minimum 10 characters required") + ` (${approvalJustification.length}/10)`}
                                error={approvalJustification.length > 0 && approvalJustification.length < 10}
                            />

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {intl.get("Attachments").d("Attachments")} ({intl.get("Optional").d("Optional")})
                                </Typography>
                                <DocumentUploadSlot
                                    docName={intl.get("AttachFile").d("Attach File")}
                                    file={null}
                                    onFileSelect={handleApprovalFileSelect}
                                    disabled={uploading || processing}
                                />

                                {approvalFiles.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                            {intl.get("UploadedFiles").d("Uploaded Files")}:
                                        </Typography>
                                        {approvalFiles.map((file) => (
                                            <Box
                                                key={file.pk}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 1,
                                                    mb: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    bgcolor: 'grey.50'
                                                }}
                                            >
                                                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                                    {file.original_filename}
                                                </Typography>
                                                <DmsButton
                                                    variant="text"
                                                    size="small"
                                                    onClick={() => handleRemoveApprovalFile(file.pk)}
                                                    disabled={processing}
                                                >
                                                    {intl.get("Remove").d("Remove")}
                                                </DmsButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </DmsModal.Body>
                    <DmsModal.Footer>
                        <DmsButton
                            variant="secondary"
                            onClick={handleCloseApproveModal}
                            disabled={processing}
                        >
                            {intl.get("Cancel").d("Cancel")}
                        </DmsButton>
                        <DmsButton
                            variant="success"
                            onClick={handleApprove}
                            disabled={processing || !approvalJustification.trim() || approvalJustification.trim().length < 10}
                            loading={processing}
                        >
                            {intl.get("Approve").d("Approve")}
                        </DmsButton>
                    </DmsModal.Footer>
                </DmsModal>

                {/* Reject Modal */}
                <DmsModal
                    isVisible={rejectModalOpen}
                    onClose={handleCloseRejectModal}
                    maxWidth="sm"
                >
                    <DmsModal.Header
                        title={intl.get("RejectRequest").d("Reject Cancellation Request")}
                        onClose={handleCloseRejectModal}
                    />
                    <DmsModal.Body>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                {intl.get("RejectRequestConfirmation").d(
                                    "Are you sure you want to reject this cancellation request? The dispensation will remain active."
                                )}
                            </Typography>
                            <DmsTextField
                                label={intl.get("RejectionJustification").d("Rejection Justification")}
                                value={rejectionJustification}
                                onChange={(e) => setRejectionJustification(e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                                required
                                helperText={intl.get("MinimumCharacters").d("Minimum 10 characters required") + ` (${rejectionJustification.length}/10)`}
                                error={rejectionJustification.length > 0 && rejectionJustification.length < 10}
                            />

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {intl.get("Attachments").d("Attachments")} ({intl.get("Optional").d("Optional")})
                                </Typography>
                                <DocumentUploadSlot
                                    docName={intl.get("AttachFile").d("Attach File")}
                                    file={null}
                                    onFileSelect={handleRejectionFileSelect}
                                    disabled={uploading || processing}
                                />

                                {rejectionFiles.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                            {intl.get("UploadedFiles").d("Uploaded Files")}:
                                        </Typography>
                                        {rejectionFiles.map((file) => (
                                            <Box
                                                key={file.pk}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 1,
                                                    mb: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    bgcolor: 'grey.50'
                                                }}
                                            >
                                                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                                    {file.original_filename}
                                                </Typography>
                                                <DmsButton
                                                    variant="text"
                                                    size="small"
                                                    onClick={() => handleRemoveRejectionFile(file.pk)}
                                                    disabled={processing}
                                                >
                                                    {intl.get("Remove").d("Remove")}
                                                </DmsButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </DmsModal.Body>
                    <DmsModal.Footer>
                        <DmsButton
                            variant="secondary"
                            onClick={handleCloseRejectModal}
                            disabled={processing}
                        >
                            {intl.get("Cancel").d("Cancel")}
                        </DmsButton>
                        <DmsButton
                            variant="danger"
                            onClick={handleReject}
                            disabled={processing || !rejectionJustification.trim() || rejectionJustification.trim().length < 10}
                            loading={processing}
                        >
                            {intl.get("Reject").d("Reject")}
                        </DmsButton>
                    </DmsModal.Footer>
                </DmsModal>
            </DmsPage>
        </DmsPermissions>
    );
};

export default DispensationCancellationRequestsPage;
